/**
 * Upcoming Meals Reminder Component
 * 
 * Displays reminder cards for the next 7 days showing:
 * - Scheduled meals for each day
 * - Daily grocery list status (no list / exists / locked)
 * - Quick actions to generate/open/update lists
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/authContext';
import menuSchedulingService from '../../services/menuSchedulingService';
import { generateGroceryListFromDay, getGroceryListsByDay } from '../../services/unifiedPlanningService';
import { getGroceryList, getOccasions } from '../../services/api';

const UpcomingMealsReminder = ({ onDaySelected, onListGenerated }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [daysData, setDaysData] = useState([]);
    const [generating, setGenerating] = useState(null); // date string

    // Calculate next 7 days starting today
    const getNext7Days = () => {
        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        for (let i = 0; i < 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            days.push(date);
        }
        return days;
    };

    useEffect(() => {
        if (user) {
            loadData();
        }
    }, [user]);

    const loadData = async () => {
        if (!user) return;

        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();

            const next7Days = getNext7Days();
            const startDate = next7Days[0];
            const endDate = next7Days[next7Days.length - 1];

            // Fetch scheduled meals for next 7 days
            const scheduledMeals = await menuSchedulingService.getScheduledMeals(
                startDate,
                endDate,
                token
            );

            // Build days data - use local date components to avoid timezone issues
            const daysDataMap = new Map();
            const dateKeys = [];
            next7Days.forEach(day => {
                // Use local date components to create dateKey
                const year = day.getFullYear();
                const month = String(day.getMonth() + 1).padStart(2, '0');
                const dayNum = String(day.getDate()).padStart(2, '0');
                const dateKey = `${year}-${month}-${dayNum}`;
                dateKeys.push(dateKey);
                daysDataMap.set(dateKey, {
                    date: day,
                    dateKey,
                    meals: [],
                    listSummary: null,
                    occasion: null
                });
            });

            // Fetch daily list summaries using local date strings
            const summariesResponse = await getGroceryListsByDay(
                dateKeys[0],
                dateKeys[dateKeys.length - 1],
                token
            );

            const summaries = summariesResponse.summaries || [];

            // Fetch occasions to show them in the upcoming meals
            let occasions = [];
            try {
                const occasionsResponse = await getOccasions(token);
                occasions = occasionsResponse.occasions || occasionsResponse || [];
            } catch (err) {
                console.error('Error loading occasions:', err);
            }

            // Group meals by date - use local date components for matching
            scheduledMeals.forEach(meal => {
                // Handle date matching - scheduled_date might be a string or Date object
                let dateKey: string;
                if (typeof meal.scheduled_date === 'string') {
                    // If it's already a date string (YYYY-MM-DD), use it directly
                    if (meal.scheduled_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        dateKey = meal.scheduled_date;
                    } else {
                        // If it's a datetime string, extract just the date part
                        dateKey = meal.scheduled_date.split('T')[0];
                    }
                } else {
                    // If it's a Date object, convert to YYYY-MM-DD using local date
                    const mealDate = new Date(meal.scheduled_date);
                    const year = mealDate.getFullYear();
                    const month = String(mealDate.getMonth() + 1).padStart(2, '0');
                    const day = String(mealDate.getDate()).padStart(2, '0');
                    dateKey = `${year}-${month}-${day}`;
                }
                
                const dayData = daysDataMap.get(dateKey);
                if (dayData) {
                    dayData.meals.push(meal);
                    
                    // If meal has occasion_id, link the occasion to this day
                    if (meal.occasion_id && !dayData.occasion) {
                        const occasion = occasions.find(occ => occ.id === meal.occasion_id);
                        if (occasion) {
                            dayData.occasion = occasion;
                        }
                    }
                }
            });

            // Add list summaries - match by date string
            summaries.forEach(summary => {
                // summary.date should be a string in YYYY-MM-DD format from backend
                const summaryDate = typeof summary.date === 'string' 
                    ? summary.date 
                    : (summary.date instanceof Date 
                        ? summary.date.toISOString().split('T')[0]
                        : new Date(summary.date).toISOString().split('T')[0]);
                const dayData = daysDataMap.get(summaryDate);
                if (dayData) {
                    dayData.listSummary = summary;
                }
            });

            setDaysData(Array.from(daysDataMap.values()));
        } catch (err) {
            console.error('Error loading upcoming meals:', err);
            setError('Failed to load upcoming meals');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateList = async (date, dateKey) => {
        if (!user) return;

        try {
            setGenerating(dateKey);
            const token = await user.getIdToken();

            console.log('Generating grocery list for date:', dateKey);
            const result = await generateGroceryListFromDay(
                dateKey,
                {},
                token
            );

            console.log('Generation result:', result);

            // Extract list ID from result
            const listId = result?.list?.id || result?.listId || (result?.list && typeof result.list === 'object' && result.list.id);
            
            if (!listId) {
                console.error('No list ID found in result:', result);
                throw new Error('Failed to generate list: No list ID returned');
            }

            console.log('Fetching full list with ID:', listId);
            
            // Always fetch the full list from the database to ensure proper structure
            const response = await getGroceryList(listId, token);
            // API returns { list: {...} }
            const fullList = response.list || response;
            
            if (!fullList || !fullList.id) {
                console.error('Invalid list structure from API:', response);
                throw new Error('Failed to fetch generated list: Invalid structure');
            }

            console.log('Fetched full list:', fullList);

            // Dispatch event to notify other pages (like Planning Hub) that a list was created
            // Include occasion_id if the list is linked to an occasion
            window.dispatchEvent(new CustomEvent('groceryListCreated', {
                detail: { 
                    listId: fullList.id, 
                    date: dateKey,
                    occasionId: fullList.occasion_id || null
                }
            }));

            // Call callbacks first to update parent component
            if (onListGenerated) {
                console.log('Calling onListGenerated with:', fullList);
                onListGenerated(fullList);
            }

            if (onDaySelected) {
                console.log('Calling onDaySelected with dateKey:', dateKey, 'listId:', fullList.id);
                onDaySelected(dateKey, fullList.id);
            }

            // Reload data to get updated summary (after callbacks to ensure UI updates)
            await loadData();
        } catch (err) {
            console.error('Error generating daily list:', err);
            alert('Failed to generate grocery list. Please try again.');
        } finally {
            setGenerating(null);
        }
    };

    const handleOpenList = async (listId, dateKey) => {
        if (!user) return;

        try {
            const token = await user.getIdToken();
            const response = await getGroceryList(listId, token);
            // API returns { list: {...} }
            const list = response.list || response;

            if (onDaySelected) {
                onDaySelected(dateKey, listId);
            }
        } catch (err) {
            console.error('Error loading list:', err);
            alert('Failed to load grocery list. Please try again.');
        }
    };

    const formatDate = (date) => {
        // Use local date components to avoid timezone issues
        const today = new Date();
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const todayDay = today.getDate();
        
        // Handle date - could be Date object or date string
        let targetDate: Date;
        if (date instanceof Date) {
            targetDate = new Date(date);
        } else if (typeof date === 'string') {
            // If it's a date string (YYYY-MM-DD), parse it as local date
            if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = date.split('-').map(Number);
                targetDate = new Date(year, month - 1, day);
            } else {
                // If it's a datetime string, extract just the date part and parse as local
                const datePart = date.split('T')[0];
                const [year, month, day] = datePart.split('-').map(Number);
                targetDate = new Date(year, month - 1, day);
            }
        } else {
            targetDate = new Date(date);
        }
        
        const targetYear = targetDate.getFullYear();
        const targetMonth = targetDate.getMonth();
        const targetDay = targetDate.getDate();

        // Compare using local date components
        if (targetYear === todayYear && targetMonth === todayMonth && targetDay === todayDay) {
            return 'Today';
        }

        const tomorrow = new Date(todayYear, todayMonth, todayDay + 1);
        if (targetYear === tomorrow.getFullYear() && 
            targetMonth === tomorrow.getMonth() && 
            targetDay === tomorrow.getDate()) {
            return 'Tomorrow';
        }

        return targetDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const getMealTypeColor = (mealType) => {
        const colors = {
            breakfast: '#FFE5B4',
            lunch: '#E8F5E9',
            dinner: '#E3F2FD',
            snack: '#F3E5F5'
        };
        return colors[mealType] || '#F5F5F5';
    };

    if (loading) {
        return (
            <div className="bg-neutral-0 p-6 hairline mb-6">
                <p className="font-sans text-base text-neutral-900">Loading upcoming meals...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-neutral-0 p-6 hairline mb-6">
                <p className="font-sans text-base text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div className="bg-neutral-0 p-4 hairline mb-4">
            <h2 className="font-sans text-xl font-medium text-[#1b263b] mb-2">
                Upcoming Meals
            </h2>
            <p className="font-sans text-xs text-neutral-600 mb-3">
                Next 7 days of scheduled meals and grocery lists
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2">
                {daysData.map((dayData) => {
                    const { date, dateKey, meals, listSummary, occasion } = dayData;
                    const hasList = listSummary?.hasList;
                    const isLocked = listSummary?.isLocked;
                    const itemCount = listSummary?.itemCount || 0;
                    const mealCount = meals.length;
                    const hasNoMeals = mealCount === 0;
                    const hasOccasion = occasion !== null;

                    return (
                        <div
                            key={dateKey}
                            className={`bg-neutral-50 hairline cursor-pointer hover:bg-neutral-100 transition-colors ${
                                hasNoMeals ? 'p-2' : 'p-3'
                            }`}
                            onClick={() => {
                                if (hasList && !isLocked) {
                                    handleOpenList(listSummary.listId, dateKey);
                                } else if (hasList && isLocked) {
                                    // Locked list - offer to create new version
                                    if (window.confirm('This list is locked. Create a new version?')) {
                                        handleGenerateList(date, dateKey);
                                    }
                                } else if (mealCount > 0) {
                                    // No list but has meals - generate
                                    handleGenerateList(date, dateKey);
                                }
                            }}
                        >
                            {/* Date Header */}
                            <div className={hasNoMeals ? 'mb-1' : 'mb-2'}>
                                <div className={`font-mono uppercase tracking-wider text-[#1b263b] ${hasNoMeals ? 'text-[10px]' : 'text-xs'}`}>
                                    {formatDate(date)}
                                </div>
                            </div>

                            {/* Occasion Badge */}
                            {hasOccasion && (
                                <div className="mb-2">
                                    <div className="text-xs px-2 py-0.5 rounded bg-[#1b263b] text-white font-mono uppercase tracking-wider">
                                        {occasion.name}
                                    </div>
                                </div>
                            )}

                            {/* Meals */}
                            {mealCount > 0 ? (
                                <div className={`space-y-1 ${hasNoMeals ? 'mb-1' : 'mb-2'}`}>
                                    {meals.slice(0, 3).map((meal, idx) => (
                                        <div
                                            key={meal.id || idx}
                                            className="text-xs px-2 py-0.5 rounded"
                                            style={{
                                                backgroundColor: getMealTypeColor(meal.meal_type),
                                                color: '#1b263b'
                                            }}
                                        >
                                            {meal.recipe_name || 'Recipe'}
                                            {meal.meal_type && (
                                                <span className="ml-1 opacity-75">
                                                    ({meal.meal_type})
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    {meals.length > 3 && (
                                        <div className="text-xs text-neutral-600">
                                            +{meals.length - 3} more
                                        </div>
                                    )}
                                </div>
                            ) : hasOccasion ? (
                                <div className={`text-xs text-neutral-600 ${hasNoMeals ? 'mb-1' : 'mb-2'}`}>
                                    Occasion: {occasion.name}
                                </div>
                            ) : (
                                <div className={`text-[10px] text-neutral-500 italic ${hasNoMeals ? 'mb-1' : 'mb-2'}`}>
                                    No meals
                                </div>
                            )}

                            {/* List Status */}
                            {!hasNoMeals && (
                                <div className={`pt-2 border-t border-neutral-200 ${hasNoMeals ? 'mt-1' : 'mt-2'}`}>
                                    {hasList ? (
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs text-neutral-600">
                                                {isLocked ? (
                                                    <span className="flex items-center gap-1">
                                                        🔒 Locked
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {itemCount} items
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-[#1b263b] text-white hover:opacity-60 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isLocked) {
                                                        if (window.confirm('Create new version of locked list?')) {
                                                            handleGenerateList(date, dateKey);
                                                        }
                                                    } else {
                                                        handleOpenList(listSummary.listId, dateKey);
                                                    }
                                                }}
                                            >
                                                {isLocked ? 'New' : 'Open'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs text-neutral-500">
                                                No list
                                            </div>
                                            {mealCount > 0 && (
                                                <button
                                                    className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleGenerateList(date, dateKey);
                                                    }}
                                                    disabled={generating === dateKey}
                                                >
                                                    {generating === dateKey ? '...' : 'Generate'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UpcomingMealsReminder;

